import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AutocompleteInput,
  Confirm,
  Create,
  FormDataConsumer,
  Loading,
  RadioButtonGroupInput,
  ReferenceInput,
  SaveButton,
  SimpleForm,
  TextInput,
  Toolbar,
  maxLength,
  minLength,
  number,
  required,
  useDataProvider,
  useGetOne,
  useNotify,
  usePermissions,
  useRecordContext,
  useRefresh
} from 'react-admin';
import { useSaveContext, useTranslate } from 'ra-core';
import { useFormContext, useWatch } from 'react-hook-form';
import { PriceInput } from '../../components';
import { BetweenDatesTimesInput } from '../../components/BetweenDatesInput';
import Permission from '../../components/Permission';
import { PERMISSIONS } from '../../constants';
import { getChainId, getPlaceId } from '../../lib';
import { PendingTransactionOriginType } from '../../models/pendingTransactionType';
import { FillinOrigin } from '../../models';
import { track, EVENTS } from '../../lib/analytics';
import RequiredChain from '../../components/RequiredChain';
import { isValidCPF } from '@brazilian-utils/brazilian-utils';
import { filterToQuery, formatNumber, strip } from '../../utils';
import dayjs from 'dayjs';
import { buildPendingTransactionPayload, findFillin } from './pendingTransaction';

const fullWrapperSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  width: '100%',
};

const rowSx = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 2,
};

const fillinBoxSx = {
  padding: '10px',
  borderWidth: 1
};

const fillinTextSx = {
  margin: '5px',
};

// O backend responde com a causa real (Boom): "Cliente não encontrado!",
// "Abastecimento já cadastrado!", "Frentista não encontrado!". Antes tudo virava
// "Erro ao adicionar pontos" e o frentista não tinha como saber o que corrigir.
const submitErrorMessage = (error) => {
  if (error?.status === 429) {
    return 'Muitas tentativas seguidas. Espere alguns segundos antes de pontuar de novo.';
  }
  const serverMessage = error?.body?.message || error?.message;
  // Erro de payload volta como "Invalid request payload input", que não ajuda ninguém.
  if (!serverMessage || error?.status === 400) {
    return 'Erro ao adicionar pontos. Confira os dados do formulário.';
  }
  return serverMessage;
};

// Espelha o filtro que o backend usa pra pontuar:
// findOneBy({ cpf, isDeleted: false, isBlocked: false }).
// A busca do admin NÃO aplica esse filtro, então um cliente bloqueado ou removido
// aparece normalmente no card e só falha na hora de pontuar.
const describeCustomerBlock = (customer, identifyCustomer) => {
  if (!customer) {
    return {
      title: 'Cliente não cadastrado nesta rede',
      text: `Não encontramos o CPF/código ${identifyCustomer} nesta rede. A pontuação não vai ser aceita. Confira o número digitado ou cadastre o cliente antes de pontuar.`,
    };
  }
  if (customer.isDeleted) {
    return {
      title: 'Cliente removido',
      text: `O cadastro de ${customer.name || identifyCustomer} foi removido desta rede e não pode receber pontos.`,
    };
  }
  if (customer.isBlocked) {
    return {
      title: 'Cliente bloqueado',
      text: `O cadastro de ${customer.name || identifyCustomer} está bloqueado nesta rede e não pode receber pontos enquanto o bloqueio existir.`,
    };
  }
  return null;
};

const validateCPFAndCode = (value) => {
  if (!value) {
    return 'Informe o CPF ou o código do cliente';
  } else if (value.length === 11) {
    if (!isValidCPF(value)) {
      return 'CPF inválido';
    }
  } else if (value.length < 4) {
    return 'O código do cliente deve ter no mínimo 4 dígitos';
  }
  return undefined;
};

const AddFillinToolbar = ({ onSuccessCallback, checkCustomer, fillins, submitRef }) => {
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [blockDialog, setBlockDialog] = useState(null);
  const [checking, setChecking] = useState(false);
  const [loading, setLoading] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();
  const form = useFormContext();
  const saveContext = useSaveContext();
  const { permissions } = usePermissions();
  const isAdmin = permissions?.includes(PERMISSIONS.ADMIN);
  // Trava síncrona contra duplo clique. `loading` não serve sozinho: o estado do
  // React só reflete na tela no render seguinte, e dois cliques rápidos entram
  // os dois antes disso. Cobre também o caminho que passa pelo diálogo de preço.
  const submittingRef = useRef(false);

  const handleSubmit = useCallback(async (value) => {
    if (submittingRef.current) { return; }
    submittingRef.current = true;
    setLoading(true);
    try {
      saveContext.save && await saveContext.save(value, {
        onSuccess: () => {
          onSuccessCallback();
          if (!isAdmin) {
            form.reset();
          } else {
            // Admin mantém CPF, posto, bico e preço pra pontuar o próximo abastecimento
            // do mesmo bico sem refazer a seleção. Só o abastecimento sai: ele acabou de
            // ser consumido, e sem limpar aqui o clique seguinte reenviava o mesmo
            // `deliveryId` com a lista já recarregada — payload incompleto, 400.
            form.setValue('deliveryId', null);
          }
          window.scrollTo(0, 0);
          track(EVENTS.POINTS_ADDED_BY_REFUEL, {
            place_id: value?.placeId ?? 'unknown',
            has_attendant: !!value?.attendantId,
          });
          notify('ra.notification.created', {
            type: 'info',
            messageArgs: { smart_count: 1 },
          });
          refresh();
          setLoading(false);
        },
        onError: (error) => {
          if (!isAdmin) {
            form.reset();
          }
          window.scrollTo(0, 0);
          notify(submitErrorMessage(error), { type: 'warning' });
          refresh();
          setLoading(false);
        }
      });
    } finally {
      submittingRef.current = false;
    }
  }, [isAdmin, saveContext.save, onSuccessCallback, form, notify, refresh]);

  const verifySubmit = async (value) => {
    // O input do abastecimento só existe quando a lista vem preenchida, então o
    // required() dele não roda e o form conseguia enviar sem escolher nada — o
    // backend exige deliveryId + fillin e devolvia 400.
    if (!value.deliveryId) {
      notify('Escolha o abastecimento que vai ser pontuado.', { type: 'warning' });
      return;
    }
    // O `deliveryId` do formulário fica velho quando a lista troca (bico, período):
    // o rádio some, mas o valor antigo continua lá e passa no required. Sem o
    // abastecimento na lista atual o payload saía sem `fillin` e o backend dava 400.
    if (!findFillin(fillins, value.deliveryId)) {
      notify('O abastecimento escolhido não está mais na lista. Escolha de novo.', { type: 'warning' });
      return;
    }

    setChecking(true);
    const { conclusive, customer } = await checkCustomer(value.identifyCustomer);
    setChecking(false);

    // Consulta inconclusiva (rede caiu, por exemplo) não bloqueia: deixa o backend decidir.
    if (conclusive) {
      const reason = describeCustomerBlock(customer, value.identifyCustomer);
      if (reason) {
        setBlockDialog(reason);
        return;
      }
    }

    if (value.pricePerLiter !== undefined && (value.pricePerLiter < 1 || value.pricePerLiter > 10)) {
      setConfirmDialog(true);
      return;
    }
    form.handleSubmit(handleSubmit)();
  }

  // Enter dentro do formulário (frentista com um campo só) não passa pelo botão e
  // enviava sem nenhuma dessas checagens. O onSubmit do form chama o mesmo guard.
  submitRef.current = verifySubmit;

  const handleConfirmDialog = () => {
    setConfirmDialog(false);
    form.handleSubmit(handleSubmit)();
  }

  const handleCloseDialog = () => {
    setConfirmDialog(false);
  }

  return (
    <Toolbar>
      <FormDataConsumer>
        {({ formData }) => (
          <>
            <SaveButton
              label="Pontuar"
              type="button"
              disabled={loading || checking}
              onClick={form.handleSubmit(verifySubmit)}
            />
            <Dialog open={!!blockDialog} onClose={() => setBlockDialog(null)}>
              <DialogTitle>{blockDialog?.title}</DialogTitle>
              <DialogContent>
                <DialogContentText>{blockDialog?.text}</DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setBlockDialog(null)} autoFocus>Entendi</Button>
              </DialogActions>
            </Dialog>
            <Confirm
              isOpen={confirmDialog}
              title="Atenção"
              content={
                <Alert
                  variant="outlined"
                  severity="warning"
                >
                  O preço por litro é <strong>{formatNumber(formData.pricePerLiter, { style: 'currency', currency: 'BRL', minimumFractionDigits: 3, maximumFractionDigits: 3 })}</strong> <br />
                  Este valor parece incorreto. Tem certeza que deseja continuar?
                </Alert>
              }
              onConfirm={handleConfirmDialog}
              onClose={handleCloseDialog}
            />
          </>
        )}
      </FormDataConsumer>
    </Toolbar>
  );
};

const FillinRenderer = () => {
  const record = useRecordContext();
  if (!record) { return null; }

  return (
    <Card style={{ margin: 15, padding: 6 }}>
      <Box component="p" sx={fillinTextSx}>
        Valor: R$ {record.total}
      </Box>
      <Box component="p" sx={fillinTextSx}>
        Litros: {record.volume}
      </Box>
      <Box component="p" sx={fillinTextSx}>
        Combustível: {record.fuel?.name}
      </Box>
      <Box component="p" sx={fillinTextSx}>
        Encerrante: {record.eTotEnd}
      </Box>
      <Box component="p" sx={fillinTextSx}>
        Data: {moment(record.dateTime).format('DD/MM/YYYY HH:mm:ss')}
      </Box>
    </Card>
  );
};

// O seletor de período grava `from`/`to` no formulário, mas a busca dos
// abastecimentos roda fora dele e lê do estado da tela.
const PeriodWatcher = ({ onChange }) => {
  const [from, to] = useWatch({ name: ['from', 'to'] });

  useEffect(() => {
    if (from && to) { onChange(from, to); }
  }, [from, to]);

  return null;
};

const AddPointsByRefuel = () => {
  const translate = useTranslate();
  const { permissions } = usePermissions();
  const isAdmin = permissions?.includes(PERMISSIONS.ADMIN);
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const notify = useNotify();
  const [hoseNumber, setHoseNumber] = useState();
  const [hoseChoices, setHoseChoices] = useState();
  const [stationId, setStationId] = useState(getPlaceId());
  const [identifyCustomer, setIdentifyCustomer] = useState();
  const [fillins, setFillins] = useState();
  const [loading, setLoading] = useState();
  const submitRef = useRef();
  const { data: chain } = useGetOne('chains', { id: getChainId() });
  const [customerInfo, setCustomerInfo] = useState();
  // Período só vale pro admin: pro frentista o backend ignora e usa a janela da rede.
  // O período inicial é calculado uma vez e é ele que vai no `defaultValues`, nunca
  // `dateFrom`/`dateTo`: o SimpleForm compara `defaultValues` por conteúdo e, quando
  // muda, dá `reset()` no formulário inteiro. Com o estado lá dentro, trocar a data
  // apagava CPF, posto, bico e preço já preenchidos.
  const [initialPeriod] = useState(() => ({
    from: dayjs().startOf('day').toISOString(),
    to: dayjs().endOf('day').toISOString(),
  }));
  const [dateFrom, setDateFrom] = useState(initialPeriod.from);
  const [dateTo, setDateTo] = useState(initialPeriod.to);
  // Bump depois de pontuar pra recarregar a lista de abastecimentos sem mexer
  // no posto nem no bico selecionado.
  const [fillinsRefreshKey, setFillinsRefreshKey] = useState(0);

  useEffect(() => {
    if (stationId && hoseNumber) {
      setFillins([]);
      setLoading('Estamos buscando os abastecimentos desse bico');
      dataProvider.getList('last-fillins', { filter: { stationId, hoseNumber, dateFrom, dateTo } })
        .then(({ data }) => {
          setLoading(false);
          if (data) {
            setFillins(data)
          }
        })
        .catch(error => {
          setLoading(false);
          console.log(error);
        });
    }
  }, [hoseNumber, dateFrom, dateTo, fillinsRefreshKey]);

  useEffect(() => {
    if (stationId) {
      setLoading('Estamos buscando os bicos do posto');
      dataProvider.getList(`stations/${stationId}/hose-numbers`, {})
        .then(({ data }) => {
          setLoading(false);
          setHoseChoices(data);
        })
        .catch(error => {
          setLoading(false);
          setHoseChoices();
          notify("Nenhum bico encontrado neste posto.")
        });
    } else {
      setHoseChoices();
    }
  }, [stationId]);

  const transform =
    useCallback(data => buildPendingTransactionPayload(data, fillins), [fillins]);

  // Reconsulta o cliente no momento do envio. A busca do useEffect é debounced e
  // pode não ter rodado ainda quando o frentista clica em Pontuar.
  // `conclusive: false` = não deu pra saber (rede fora), aí não bloqueia.
  const checkCustomer = useCallback(async (identify) => {
    const stripped = strip(identify || '');
    if (!stripped) { return { conclusive: true, customer: null }; }

    try {
      const { data } = await dataProvider.getOne('chains/chainId/customers', { id: stripped });
      return { conclusive: true, customer: data };
    } catch (error) {
      if (error?.status === 404) { return { conclusive: true, customer: null }; }
      return { conclusive: false, customer: null };
    }
  }, [dataProvider]);

  // Antes daqui saía posto, bico, lista de bicos e lista de abastecimentos, e a
  // lista de bicos não voltava: o effect que a carrega depende de `stationId`,
  // que era zerado e reposto no mesmo tick — o React agrupa as duas chamadas, o
  // valor final é o mesmo de antes e o effect não roda de novo. Resultado: pra
  // pontuar o próximo abastecimento era preciso recarregar a tela.
  // Agora posto e bico ficam, e só a lista de abastecimentos é recarregada — o
  // que acabou de ser pontuado sai dela sozinho, porque o /last-fillins descarta
  // encerrante que já existe em `fillin` ou `pending_fillin`.
  const onSuccess = () => {
    if (isAdmin) {
      setFillinsRefreshKey((key) => key + 1);
      return;
    }

    // O frentista tem o formulário resetado no toolbar (terminal compartilhado,
    // o CPF do cliente anterior não pode ficar na tela). O estado local
    // acompanha, senão sobraria bico marcado sem seleção no formulário.
    setHoseNumber(undefined);
    setFillins(null);
    setCustomerInfo(null);
  };

  useEffect(() => {
    setCustomerInfo(null);

    if (!identifyCustomer) { return; }

    const strippedValue = strip(identifyCustomer);
    const isCPF = strippedValue.length === 11;
    const isCode = strippedValue.length >= 4 && strippedValue.length < 11;

    if ((isCPF && isValidCPF(strippedValue)) || isCode) {
      const timeoutId = setTimeout(() => {
        dataProvider.getOne('chains/chainId/customers', { id: strippedValue })
          .then(({ data }) => {
            setCustomerInfo(data);
          })
          .catch(() => {
            notify(
              isCPF
                ? 'CPF não encontrado'
                : 'Código do cliente não encontrado',
              { type: 'warning' }
            );
          });
      }, 900);

      return () => clearTimeout(timeoutId);
    }
  }, [identifyCustomer]);

  const CustomerCard = () => (
    <Card variant="outlined" sx={{ marginBottom: '10px' }}>
      <CardContent sx={{ padding: '20px!important' }}>
        <Typography variant="caption" dangerouslySetInnerHTML={{ __html: `O CPF/Código informado corresponde ao cliente <strong>${customerInfo.name || '[sem nome]'}</strong>` }} />
      </CardContent>
    </Card>
  );

  return (
    <Permission permission={PERMISSIONS.ADD_POINTS_BY_FUEL}>
      <Create title="Adicionar Pontos" redirect={false} resource={`chains/chainId/pending-transaction`} transform={transform}>
        <SimpleForm loading={loading} onSubmit={(values) => submitRef.current?.(values)} defaultValues={{ stationId: getPlaceId(), origin: PendingTransactionOriginType.linkedFillin, originFillin: FillinOrigin.manuallyAdded, from: initialPeriod.from, to: initialPeriod.to }} toolbar={<AddFillinToolbar onSuccessCallback={onSuccess} checkCustomer={checkCustomer} fillins={fillins} submitRef={submitRef} />}>
          <Box sx={fullWrapperSx}>
            {/* Linha 1: Código do cliente + Posto */}
            <Box sx={rowSx}>
              <TextInput
                source="identifyCustomer"
                label="Código do cliente ou CPF"
                sx={{ width: { xs: '100%', sm: 280 } }}
                placeholder="Digite apenas números"
                parse={v => strip(v)}
                isRequired
                onChange={(e) => setIdentifyCustomer(e.target.value)}
                validate={[validateCPFAndCode]}
              />
              {!getPlaceId() &&
                (<ReferenceInput
                  source="stationId"
                  filter={{ chainId: getChainId() }}
                  reference="stations"
                  perPage={null}
                  sort={{ field: 'name', order: 'ASC' }}
                >
                  <AutocompleteInput
                    label="Posto"
                    sx={{ width: { xs: '100%', sm: 280 } }}
                    filterToQuery={filterToQuery}
                    onChange={(value) => setStationId(value)}
                    optionText="name"
                    validate={required("Posto é obrigatório")}
                    noOptionsText={translate('ra.input.autocomplete.no_options_text')}
                  />
                </ReferenceInput>)
              }
            </Box>
            {/* Linha 2: Data de Início + Data Final */}
            {
              Permission({
                permission: PERMISSIONS.ADMIN,
                children: (
                  <>
                    <BetweenDatesTimesInput />
                    <PeriodWatcher
                      onChange={(from, to) => {
                        setDateFrom(from);
                        setDateTo(to);
                      }}
                    />
                  </>
                )
              })
            }
            {/* Linha 3: Código do frentista + Preço por litro */}
            {(isAdmin || (chain && chain.useAttendantCode)) &&
              <Box sx={rowSx}>
                {
                  (chain && chain.useAttendantCode) &&
                  <TextInput
                    source="attendantCode"
                    label="Código do frentista"
                    inputProps={{
                      maxLength: 4
                    }}
                    validate={[required(), minLength(4), maxLength(4), number('Somente números')]}
                    sx={{ width: { xs: '100%', sm: 280 } }}
                  />
                }
                {
                  Permission({
                    permission: PERMISSIONS.ADMIN,
                    children: (
                      <PriceInput
                        source="pricePerLiter"
                        precision="3"
                        validate={required()}
                        label="Preço por litro"
                        sx={{ width: { xs: '100%', sm: 280 } }}
                      />
                    )
                  })
                }
              </Box>
            }
          </Box>
          {
            customerInfo &&
            <CustomerCard />
          }
          <div>
            {stationId && hoseChoices &&
              <RadioButtonGroupInput
                source="hose"
                optionText="hoseNumber"
                label="Bicos"
                choices={hoseChoices}
                validate={required("Bico é obrigatório")}
                onChange={(event) => setHoseNumber(event.target.value)}
              />
            }
          </div>
          {!loading && fillins && fillins.length === 0 &&
            <Typography fullWidth variant="body1" component="h4">
              Não encontramos abastecimentos recentes pra esse bico.
            </Typography>
          }
          {loading && <Loading loadingSecondary={loading} />}
          {fillins && fillins.length > 0 &&
            <RadioButtonGroupInput
              sx={fillinBoxSx}
              fullWidth
              label="Escolha um Abastecimento"
              source="deliveryId"
              choices={fillins}
              validate={required("Escolha um Abastecimento")}
              optionText={<FillinRenderer record={{}} />}
            />
          }
        </SimpleForm>
      </Create>
    </Permission>
  );
};

export default (props) => (
  <RequiredChain>
    <AddPointsByRefuel {...props} />
  </RequiredChain>
);